/**
 * The mouth as an emitter: words leave the lips and drift away.
 *
 * The other two views hold the line together — one pinned to a column, one
 * pinned to the mouth — and both read as a caption that happens to be near a
 * face. This one lets go. A word is launched from the lip centroid at the moment
 * it was spoken, with a speed taken from how far the mouth was actually open,
 * and then it is on its own: it drifts, it grows, it fades.
 *
 * SPEC1.md files this under "later: breath emission" — particles spawned at the
 * lip centroid, velocity from aperture. The particles here are the words
 * themselves, which is cheaper than it sounds: they are the same spans the
 * caption builds, carrying the same per-glyph axes, so a word emitted from a
 * wide "ah" is both heavier *and* faster, from two independent readings of the
 * same mouth.
 *
 * ### Everything is measured in inter-ocular units
 *
 * Speed, gravity and size are all multiples of the distance between the
 * speaker's eyes, converted to pixels at launch. A number in pixels would make
 * the piece behave differently on a phone than on a monitor, and differently
 * again when the speaker leaned in — the motion has to be a property of the
 * person, not of the window. This is the same reasoning that normalises every
 * feature in Phase 1.
 *
 * ### Transform and opacity only
 *
 * Nothing here touches layout after launch. Each word is positioned once, then
 * animated purely by `transform` and `opacity`, so ninety live words cost the
 * compositor and not the layout engine. Writing `left`/`top` instead would
 * reflow the page ninety times a frame.
 */

const RAD = Math.PI / 180;

/**
 * Advance one word by `dt` seconds.
 *
 * Pure, and exported because it is the only part of this file with a claim in
 * it. Everything else is bookkeeping and DOM.
 *
 * @param {object} p particle state, mutated
 * @param {number} dt seconds
 * @param {object} cfg config.emit
 */
export function step(p, dt, cfg) {
  // Solved rather than stepped.
  //
  // `v' = -λv + g` has a closed form, and using it is what makes the motion
  // identical at 30 fps and at 120. Decaying the velocity per frame and then
  // adding `v·dt` gets the velocity right and the *position* wrong — over one
  // long frame it samples an exponentially decaying speed exactly once, so a
  // word on a slow machine travels a third as far as the same word on a fast
  // one. Which is a difference you would notice on the phone and never
  // reproduce on the laptop.
  const g = cfg.GRAVITY * p.iod;
  const lambda = -Math.log(Math.min(1, Math.max(1e-6, cfg.DRAG)));

  if (lambda < 1e-6) {
    // No drag: ordinary ballistics, and the exponential form would divide by
    // zero getting there.
    p.x += p.vx * dt;
    p.y += p.vy * dt + 0.5 * g * dt * dt;
    p.vy += g * dt;
  } else {
    const decay = Math.exp(-lambda * dt);
    const travel = (1 - decay) / lambda;
    // Terminal velocity — the speed at which drag balances gravity.
    const vTerm = g / lambda;

    p.x += p.vx * travel;
    p.y += vTerm * dt + (p.vy - vTerm) * travel;
    p.vx *= decay;
    p.vy = vTerm + (p.vy - vTerm) * decay;
  }

  p.age += dt;
  return p;
}

/**
 * How a word looks at a given point in its life.
 *
 * The fade-in is fast and the fade-out is not: a word has to be legible for most
 * of its travel or the whole thing is a texture rather than a sentence.
 *
 * @returns {{opacity: number, scale: number, done: boolean}}
 */
export function appearance(p, cfg) {
  const t = p.age / p.life;
  if (t >= 1) return { opacity: 0, scale: cfg.SCALE_TO, done: true };
  // Guarded rather than clamped: FADE_IN is a control and 0 is a legitimate
  // setting for it, but 0/0 on the very first frame is NaN and an element with
  // `opacity: NaN` does not render at all.
  const fadeIn = cfg.FADE_IN > 0 ? Math.min(1, t / cfg.FADE_IN) : 1;
  const fadeOut = (1 - t) ** cfg.FADE_OUT;
  return {
    opacity: fadeIn * fadeOut * cfg.OPACITY,
    scale: cfg.SCALE_FROM + (cfg.SCALE_TO - cfg.SCALE_FROM) * t,
    done: false,
  };
}

/**
 * The type size a glyph leaves the mouth at, in pixels.
 *
 * One function rather than one expression written twice, because the probe
 * parks a glyph at this size to preview it — and a preview that drifts from the
 * thing it previews is worse than no preview.
 *
 * @param {number} iod inter-ocular distance in screen pixels
 * @param {object} cfg config.emit
 */
export function glyphSize(iod, cfg) {
  return iod * cfg.SCALE_FROM * cfg.TYPE_SCALE;
}

/**
 * Where a particle actually starts, given where the mouth is.
 *
 * The offset and the jitter radius are both in inter-ocular units, so the
 * stream keeps its relationship to the face as the speaker moves rather than
 * sliding off it. Pure, and the randomness is injected, because "somewhere near
 * the mouth" is not a thing a test can assert about.
 *
 * @param {{x: number, y: number, iod: number}} at
 * @param {object} cfg config.emit
 * @param {() => number} [rand]
 */
export function origin(at, cfg, rand = Math.random) {
  const angle = rand() * 2 * Math.PI;
  const radius = rand() * cfg.JITTER * at.iod;
  return {
    x: at.x + at.iod * cfg.ORIGIN_X + Math.cos(angle) * radius,
    y: at.y + at.iod * cfg.ORIGIN_Y + Math.sin(angle) * radius,
  };
}

/**
 * A launch velocity, in pixels per second.
 *
 * @param {number} aperture 0..1, how far the mouth was open when this was said
 * @param {number} iod inter-ocular distance in screen pixels
 * @param {object} cfg config.emit
 * @param {() => number} [rand]
 */
export function launch(aperture, iod, cfg, rand = Math.random) {
  // The floor under the aperture term is a control now — see DRIVE_FLOOR. A
  // consonant-heavy word can be spoken with a mouth that barely opens, and a
  // word with no velocity at all does not leave: it sits on the lips and fades,
  // which looks like a bug rather than like a quiet word.
  const floor = cfg.DRIVE_FLOOR;
  const drive = floor + (1 - floor) * Math.min(1, Math.max(0, aperture));
  const angle = (cfg.ANGLE + (rand() * 2 - 1) * cfg.SPREAD) * RAD;
  const speed = cfg.SPEED * iod * drive;
  return {
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    // Degrees per second, either way. Read off `age` in `frame` rather than
    // integrated, so a tumble adds no state to step().
    vrot: (rand() * 2 - 1) * cfg.SPIN,
  };
}

export class Emitter {
  /**
   * @param {HTMLElement} layer  positioned container the words live in
   * @param {object} [o]
   * @param {object} [o.config]  config.emit, read at use time
   */
  constructor(layer, { config } = {}) {
    this.layer = layer;
    this._config = config;
    /** @type {object[]} */
    this.particles = [];
    this._last = 0;
  }

  get cfg() {
    return this._config;
  }

  get count() {
    return this.particles.length;
  }

  /**
   * Launch an element from a point.
   *
   * @param {HTMLElement} el  already built, not yet in the document
   * @param {object} at  {x, y, iod} in layer pixels
   * @param {number} aperture 0..1
   *
   * A word's age is counted from the first frame that moves it, not from a
   * launch timestamp: the two differ by however long the frame took, and taking
   * the timestamp here would make a word launched just after a stutter start its
   * life already partly spent.
   */
  emit(el, at, aperture) {
    const cfg = this.cfg;
    const { vx, vy, vrot } = launch(aperture, at.iod, cfg);
    const from = origin(at, cfg);

    el.className = `${el.className} emit-word`.trim();
    // Sized relative to the face, like everything else on this view.
    el.style.fontSize = `${glyphSize(at.iod, cfg)}px`;
    this.layer.append(el);

    this.particles.push({
      el,
      x: from.x,
      y: from.y,
      vx,
      vy,
      vrot,
      iod: at.iod,
      age: 0,
      life: cfg.LIFE,
    });

    // Oldest first, because they are the closest to gone anyway. A hard cap
    // rather than an unbounded layer: a long take would otherwise accumulate
    // every word ever said as an invisible, still-animating element.
    while (this.particles.length > cfg.MAX) this._retire(this.particles.shift());
    return el;
  }

  _retire(p) {
    p.el.remove();
  }

  /**
   * Advance every live word. Call once per frame.
   *
   * @param {number} now performance.now()
   */
  frame(now = performance.now()) {
    // Clamped, because a backgrounded tab returns with a gap of seconds and an
    // unclamped step would teleport every word off screen at once.
    const dt = this._last ? Math.min(0.05, (now - this._last) / 1000) : 0;
    this._last = now;
    if (!this.particles.length) return;

    const cfg = this.cfg;
    const keep = [];
    for (const p of this.particles) {
      step(p, dt, cfg);
      const look = appearance(p, cfg);
      if (look.done) {
        this._retire(p);
        continue;
      }
      // The second translate centres the word on its own launch point; without
      // it a long word hangs off to the right of the mouth rather than out of
      // it. Order matters — it is applied in the element's own space.
      const spin = p.vrot ? ` rotate(${(p.vrot * p.age).toFixed(1)}deg)` : '';
      p.el.style.transform =
        `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) ` +
        `translate(-50%, -50%) scale(${look.scale.toFixed(3)})${spin}`;
      p.el.style.opacity = look.opacity.toFixed(3);
      keep.push(p);
    }
    this.particles = keep;
  }

  clear() {
    for (const p of this.particles) this._retire(p);
    this.particles = [];
    this._last = 0;
  }
}

export default Emitter;
